import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';

import {
  getAdSpendPercentage,
  getSubscriptionDiscount,
} from '@app/client/settings/shared/modules/subscription/subscription.utilities';
import { AdPlatformType } from '@app/core/models/common.models';
import {
  AdspertPlanType,
  BillingVolumeType,
  CustomerSubscription,
  InvoiceCurrency,
  SubscriptionInvoicePeriod,
} from '@app/core/models/customer.models';
import { months } from '@app/core/services/utilities.service';
import { numberToMicros } from '@app/core/utilities/number.utilities';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

interface ExampleAccountMonth {
  account: AdPlatformType;
  adSpend?: number;
  paidSales?: number;
}

interface ExamplePlan {
  fee: number;
  firstMonth: ExampleAccountMonth[];
  secondMonth: ExampleAccountMonth[];
}

@Component({
  standalone: false,
  selector: 'adspt-pricing-examples',
  templateUrl: 'pricing-examples.component.html',
  styleUrls: ['pricing-examples.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PricingExamplesComponent {
  readonly ts = inject(TranslateService);

  @Input()
  set previewSubscriptions(subscriptions: CustomerSubscription[]) {
    this.previewSubscriptions$.next(subscriptions);
  }

  readonly previewSubscriptions$ = new BehaviorSubject<CustomerSubscription[]>([]);

  readonly currency$: Observable<InvoiceCurrency> = this.previewSubscriptions$.pipe(
    map(([subscription]) => subscription?.currency || 'EUR'),
  );

  readonly months = months[this.ts.currentLang.toUpperCase()];

  readonly pricingExamples$: Observable<Partial<Record<AdspertPlanType, ExamplePlan>>> = combineLatest([
    this.previewSubscriptions$,
    this.currency$,
  ]).pipe(
    map(([subscriptions, currency]) =>
      subscriptions.reduce((acc, subscription) => {
        const plan = subscription.type;

        acc[plan] = {
          fee: subscription.pricingDetails?.fixedFee,
        };

        if (plan === AdspertPlanType.Essential) {
          acc[plan].firstMonth = [
            {
              account: AdPlatformType.Amazon,
              paidSales: numberToMicros({ EUR: 7000, USD: 7500, GBP: 6000 }[currency]),
            },
            {
              account: AdPlatformType.Amazon,
              paidSales: numberToMicros({ EUR: 7000, USD: 7500, GBP: 6000 }[currency]),
            },
          ];
          acc[plan].secondMonth = [
            {
              account: AdPlatformType.Amazon,
              paidSales: numberToMicros({ EUR: 7000, USD: 7500, GBP: 6000 }[currency]),
            },
            {
              account: AdPlatformType.Amazon,
              paidSales: numberToMicros({ EUR: 7000, USD: 7500, GBP: 6000 }[currency]),
            },
            { account: AdPlatformType.Ebay, paidSales: numberToMicros({ EUR: 5000, USD: 5000, GBP: 4000 }[currency]) },
          ];
        } else if (plan === AdspertPlanType.Scale) {
          acc[plan].firstMonth = [
            {
              account: AdPlatformType.Amazon,
              paidSales: numberToMicros({ EUR: 7000, USD: 7500, GBP: 5000 }[currency]),
            },
            {
              account: AdPlatformType.Amazon,
              paidSales: numberToMicros({ EUR: 7000, USD: 7500, GBP: 5000 }[currency]),
            },
            { account: AdPlatformType.Google, adSpend: numberToMicros({ EUR: 4000, USD: 4000, GBP: 4000 }[currency]) },
            {
              account: AdPlatformType.Microsoft,
              adSpend: numberToMicros({ EUR: 2500, USD: 2500, GBP: 500 }[currency]),
            },
            {
              account: AdPlatformType.Microsoft,
              adSpend: numberToMicros({ EUR: 2500, USD: 2500, GBP: 500 }[currency]),
            },
          ];
          acc[plan].secondMonth = [
            {
              account: AdPlatformType.Amazon,
              paidSales: numberToMicros({ EUR: 10000, USD: 10000, GBP: 10000 }[currency]),
            },
            {
              account: AdPlatformType.Amazon,
              paidSales: numberToMicros({ EUR: 10000, USD: 10000, GBP: 10000 }[currency]),
            },
            { account: AdPlatformType.Google, adSpend: numberToMicros({ EUR: 4000, USD: 4000, GBP: 4000 }[currency]) },
            {
              account: AdPlatformType.Microsoft,
              adSpend: numberToMicros({ EUR: 2500, USD: 2500, GBP: 2500 }[currency]),
            },
            {
              account: AdPlatformType.Microsoft,
              adSpend: numberToMicros({ EUR: 2500, USD: 2500, GBP: 2500 }[currency]),
            },
          ];
        } else if (plan === AdspertPlanType.Pro) {
          acc[plan].firstMonth = [
            {
              account: AdPlatformType.Amazon,
              paidSales: numberToMicros({ EUR: 7000, USD: 7500, GBP: 4500 }[currency]),
            },
            {
              account: AdPlatformType.Amazon,
              paidSales: numberToMicros({ EUR: 7000, USD: 7500, GBP: 4500 }[currency]),
            },
            { account: AdPlatformType.Google, adSpend: numberToMicros({ EUR: 4000, USD: 4000, GBP: 4000 }[currency]) },
            {
              account: AdPlatformType.Microsoft,
              adSpend: numberToMicros({ EUR: 2500, USD: 2500, GBP: 500 }[currency]),
            },
            {
              account: AdPlatformType.Microsoft,
              adSpend: numberToMicros({ EUR: 2500, USD: 2500, GBP: 500 }[currency]),
            },
          ];
          acc[plan].secondMonth = [
            {
              account: AdPlatformType.Amazon,
              paidSales: numberToMicros({ EUR: 10000, USD: 10000, GBP: 10000 }[currency]),
            },
            {
              account: AdPlatformType.Amazon,
              paidSales: numberToMicros({ EUR: 10000, USD: 10000, GBP: 10000 }[currency]),
            },
            { account: AdPlatformType.Google, adSpend: numberToMicros({ EUR: 4000, USD: 4000, GBP: 4000 }[currency]) },
            {
              account: AdPlatformType.Microsoft,
              adSpend: numberToMicros({ EUR: 2500, USD: 2500, GBP: 2500 }[currency]),
            },
            {
              account: AdPlatformType.Microsoft,
              adSpend: numberToMicros({ EUR: 2500, USD: 2500, GBP: 2500 }[currency]),
            },
          ];
        }

        return acc;
      }, {}),
    ),
  );

  getAccountsLabel(accounts: ExampleAccountMonth[]): string {
    return Object.entries(
      accounts.reduce(
        (accountMap, { account }) => ({
          ...accountMap,
          [account]: (accountMap[account] || 0) + 1,
        }),
        {} as Record<AdPlatformType, number>,
      ),
    )
      .map(([account, count]) =>
        this.ts.instant(
          `settings.customer-subscription.${count}-${account.toLowerCase()}-account${count > 1 ? 's' : ''}`,
        ),
      )
      .join(' + ');
  }

  getPaidSales(accounts: ExampleAccountMonth[]): number[] {
    return this.getTotalFromAccount(accounts, 'paidSales');
  }

  getScale(
    accounts: ExampleAccountMonth[],
    plan: CustomerSubscription,
  ): {
    percentage: number;
    threshold: number;
    ceiling: number;
  } {
    const totalPaidSales = this.getPaidSales(accounts).reduce((total, paidSales) => total + paidSales, 0);

    const percentRanges =
      plan.pricingDetails?.volumePercentage?.find(
        (volumePercentage) => volumePercentage.volumeType === BillingVolumeType.AttributedSales,
      )?.tiers || [];

    const percentRangeIdx = percentRanges.findIndex((range) => totalPaidSales < range.threshold);
    const threshold = percentRanges[percentRangeIdx - 1]?.threshold || 0;

    return {
      percentage: percentRanges[percentRangeIdx].percentage,
      threshold: threshold,
      ceiling: percentRanges[percentRangeIdx]?.threshold,
    };
  }

  getAdSpendPercentage(plan: CustomerSubscription): number {
    return getAdSpendPercentage(plan);
  }

  getAdSpend(accounts: ExampleAccountMonth[]): number[] {
    return this.getTotalFromAccount(accounts, 'adSpend');
  }

  getFinalPriceCalculation(planData: ExamplePlan, month: 'firstMonth' | 'secondMonth', plan: CustomerSubscription) {
    const totalPaidSales = this.getPaidSales(planData[month]).reduce((total, paidSales) => total + paidSales, 0) || 0;
    const totalAdSpend = this.getAdSpend(planData[month]).reduce((total, adSpend) => total + adSpend, 0) || 0;
    const scale = this.getScale(planData[month], plan);

    return {
      fee: planData.fee,
      adSpend: totalAdSpend,
      paidSales: totalPaidSales,
      percentage: scale.percentage,
    };
  }

  getFinalPrice(
    planData: ExamplePlan,
    month: 'firstMonth' | 'secondMonth',
    period: SubscriptionInvoicePeriod,
    plan: CustomerSubscription,
  ): number {
    const { fee, adSpend, paidSales, percentage } = this.getFinalPriceCalculation(planData, month, plan);

    const invoice = fee + paidSales * percentage + adSpend * this.getAdSpendPercentage(plan);

    const discount = getSubscriptionDiscount(plan, period);

    return invoice * (1 - discount);
  }

  private getTotalFromAccount(accounts: ExampleAccountMonth[], property: 'adSpend' | 'paidSales'): number[] {
    return Object.values(
      accounts.reduce(
        (map, account) => ({
          ...map,
          [account.account]: (map[account.account] || 0) + (account[property] || 0),
        }),
        {} as Record<AdPlatformType, number>,
      ),
    ).filter((value) => value > 0);
  }

  protected readonly AdspertPlanType = AdspertPlanType;

  protected readonly SubscriptionInvoicePeriod = SubscriptionInvoicePeriod;
  protected readonly getSubscriptionDiscount = getSubscriptionDiscount;
}
